import { Trans, t } from "@lingui/macro";
import { Star, Trash2, Loader2, Play, CircleStop } from "lucide-react";
import { useEffect, useReducer, useState } from "react";
import { Soribot } from "../../models/Soribot";
import { SoribotTestRun, TestAnswer } from "../../models/SoribotTestCase";
import { Fire } from "../../services/FireClient";
import { UtilApp } from "../../services/Utils/UtilApp";
import BotSourceTextModal from "../rooms/right-menu/BotSourceTextModal";
import BotMessageInfoModal from "./BotMessageInfoModal";
import { TestRunExportModal } from "./compontents/TestRunExportModal";
import { Util } from "../../services/Utils/Util";
import { My } from "../../services/My";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { ScrollArea } from "../../components/ui/scroll-area";
import { useToast } from "../../hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

var selectedRun: SoribotTestRun | undefined; // Must be global so that the subscribe closure can access it.

const SoribotTestRuns: React.FC<{ bot: Soribot }> = ({ bot }) => {
  const [inProgress, setInProgress] = useState(false);
  const [testRuns, setTestRuns] = useState<SoribotTestRun[]>([]);
  const { toast } = useToast();
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [alert, setAlert] = useState<{ open: boolean, title: string, message: React.ReactNode, confirm: (() => void) | null }>({ open: false, title: '', message: '', confirm: null });
  const [botVersionDialog, setBotVersionDialog] = useState<any | null>(null);

  useEffect(() => {
    setTestRuns([]);
    setInProgress(true);
    selectedRun = undefined;
    return Fire.bot.subscribeToTestRuns(bot.id, runs => {
      setTestRuns(runs);
      setInProgress(false);
      const ongoing = runs.find(r => r.isRunning || r.isEvaluating);
      selectedRun = ongoing || (selectedRun ? runs.find(r => r.id === selectedRun?.id) : runs[0]);
    });
  }, [bot])

  function getScoreAsStarIcons(nrOfStars: number | null) {
    if (!nrOfStars || isNaN(nrOfStars) || nrOfStars < 0 || nrOfStars > 5)
      return null;
    else
      return (
        <div className="flex">
          {Array.from(Array(Math.round(nrOfStars)).keys()).map(i => (
            <Star key={i} className="w-3 h-3 text-yellow-500 fill-current" />
          ))}
        </div>
      );
  }

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-200px)] gap-6">
      <div className="lg:w-1/3 border rounded bg-white flex flex-col">
        {inProgress && <div className="p-4 flex justify-center"><Loader2 className="animate-spin text-gray-500" /></div>}
        <div className="p-3 border-b bg-gray-50 font-semibold text-sm">
          <Trans>Test runs</Trans>
        </div>
        <ScrollArea className="flex-1">
          {testRuns.length === 0 && !inProgress && <div className="p-4 text-center text-gray-500 text-sm"><Trans>Start test runs from page "Test cases".</Trans></div>}
          <div className="divide-y">
            {testRuns.map(tr => <TestRunItem key={tr.id} run={tr} onChange={changeRun} />)}
          </div>
        </ScrollArea>
      </div>

      <div className="lg:w-2/3 border rounded bg-white overflow-hidden flex flex-col">
        {selectedRun ? (
          <div className="h-full flex flex-col">
            <div className="p-4 border-b bg-gray-50">
              <div className="flex items-center justify-between">
                <h5 className="font-semibold text-lg truncate flex-1 mr-4">{selectedRun.testCase.title}</h5>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={deleteRun}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <TestRunExportModal run={selectedRun} />
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1">{UtilApp.dateStrToTimeAndDay(selectedRun.created, My.user.lang)}</div>

              <div className="flex items-center gap-3 mt-4">
                {!selectedRun.isRunning && !selectedRun.isEvaluating && <Button onClick={startEvaluation} disabled={inProgress} size="sm"><Play className="w-3 h-3 mr-2" /><Trans>Evaluate answers</Trans></Button>}
                {(selectedRun.isRunning || selectedRun.isEvaluating) && <Button onClick={stopProcess} variant="destructive" size="sm"><CircleStop className="w-3 h-3 mr-2" /><Trans>Stop</Trans></Button>}
                {selectedRun.soribot && <Button variant="outline" size="sm" onClick={viewSoribot}><Trans>Bot Version</Trans></Button>}
              </div>
              {selectedRun.error && <div className="mt-2 text-red-500 text-sm p-2 bg-red-50 rounded">{selectedRun.error}</div>}
            </div>

            <ScrollArea className="flex-1 p-4 bg-gray-50/50">
              <div className="space-y-4">
                {selectedRun.answers.map((a, i) => getAnswerItem(a, i))}
              </div>
            </ScrollArea>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-400">
            <Trans>Select a test run to view details</Trans>
          </div>
        )}
      </div>

      <AlertDialog open={alert.open} onOpenChange={(open) => setAlert(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alert.title}</AlertDialogTitle>
            <AlertDialogDescription>{alert.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {alert.confirm ?
              <>
                <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
                <AlertDialogAction onClick={() => { alert.confirm?.(); setAlert(prev => ({ ...prev, open: false })) }}><Trans>Confirm</Trans></AlertDialogAction>
              </> :
              <AlertDialogAction onClick={() => setAlert(prev => ({ ...prev, open: false }))}><Trans>OK</Trans></AlertDialogAction>
            }
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!botVersionDialog} onOpenChange={(open) => !open && setBotVersionDialog(null)}>
        <DialogContent className="max-w-3xl overflow-hidden max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle><Trans>Bot Version</Trans></DialogTitle>
            <DialogDescription><Trans>Snapshot of bot configuration</Trans></DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 border p-2 rounded bg-gray-50 text-xs font-mono">
            <pre>{botVersionDialog}</pre>
          </ScrollArea>
          <DialogFooter>
            <Button onClick={() => setBotVersionDialog(null)}><Trans>Close</Trans></Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  function getAnswerItem(a: TestAnswer, i: number) {
    return <Card key={i} className="overflow-hidden">
      <CardHeader className="p-3 bg-white border-b">
        <div className="flex items-start justify-between">
          <CardTitle className="text-sm font-medium leading-relaxed">{a.question}</CardTitle>
          <div className="flex gap-1 shrink-0 ml-2">
            {a.botMessage && <BotMessageInfoModal bmsg={a.botMessage} botMessageId={null} />}
            {a.sources && <BotSourceTextModal text={a.sources} />}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-3">
        <div className="text-sm text-gray-700 whitespace-pre-wrap">{a.answer ?? a.botMessage?.llmResponse}</div>
        {getSpinner(a)}
        {a.feedback && <div className="mt-3 p-3 bg-gray-50 rounded border text-xs">
          <div className="mb-1">{getScoreAsStarIcons(a.score)}</div>
          <div className="text-gray-600 whitespace-pre-wrap">{a.feedback}</div>
        </div>}
      </CardContent>
    </Card>
  }

  function changeRun(run: SoribotTestRun | undefined) {
    selectedRun = run;
    forceUpdate();
  }

  function deleteRun() {
    if (!selectedRun) return;

    setAlert({
      open: true,
      title: t`Delete test run`,
      message: t`Are you sure you want to delete this test run?`,
      confirm: () => {
        Fire.bot.deleteTestRun(selectedRun!.testCase.soribotId, selectedRun!.id)
          .then(() => {
            toast({ title: t`Test run deleted` });
            changeRun(undefined);
          });
      }
    });
  }

  async function startEvaluation() {
    if (selectedRun) {
      setInProgress(true);
      await Fire.bot.evaluateTestRun(bot.id, selectedRun?.id);
      setInProgress(false);
    }
  }

  function getSpinner(a: TestAnswer) {
    const isWaitingForAnswer = selectedRun?.isRunning && !selectedRun?.error && !a.completed;
    const isWaitingForEvaluation = selectedRun?.isEvaluating && a.score === null;
    if (isWaitingForAnswer || isWaitingForEvaluation) {
      return <div className="mt-2"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
    }
  }

  function stopProcess(): void {
    if (selectedRun)
      Fire.bot.stopProcess(selectedRun);
  }

  function viewSoribot() {
    if (!selectedRun?.soribot) return;
    const bot = Util.pojo(selectedRun.soribot);
    bot.owner = undefined;
    bot.rooms = undefined;
    bot.roomIds = undefined;
    bot.userIds = undefined;
    bot.welcomeMessage = undefined;
    bot.vectorServerIP = undefined;
    bot.imageUrl = undefined;

    setBotVersionDialog(JSON.stringify(bot, null, 2));
  }
}

const TestRunItem: React.FC<{ run: SoribotTestRun, onChange: (run: SoribotTestRun) => void }> = ({ run, onChange }) => {
  const [avgScore, setAvgScore] = useState<number | null>(null);
  useEffect(() => {
    setAvgScore(getEvaluationAverageScore(run.answers));
  }, [run.answers]);

  const isSelected = selectedRun?.id === run.id;

  return <div
    onClick={() => onChange(run)}
    className={`p-3 border-b cursor-pointer hover:bg-gray-50 flex items-center justify-between transition-colors ${isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'border-l-4 border-l-transparent'}`}
  >
    <div className="flex-1 min-w-0 pr-2">
      <div className="truncate text-sm font-medium">{run.testCase.title}</div>
      <div className="text-xs text-gray-500 mt-1">{UtilApp.dateStrToTimeAndDay(run.created, My.user.lang)}</div>
    </div>
    <div className="shrink-0 flex items-center">
      {(run.isRunning || run.isEvaluating) && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
      {avgScore && <span className="text-sm font-semibold flex items-center text-yellow-600 ml-2">{avgScore.toFixed(1)} <Star className="w-3 h-3 ml-1 fill-current" /></span>}
    </div>
  </div>
}

function getEvaluationAverageScore(answers: TestAnswer[]): number | null {
  if (!answers)
    return null;

  let total = 0;
  let count = 0;

  answers.forEach(a => {
    if (a.score !== null) {
      total += a.score;
      count++;
    }
  });
  return count > 0 ? total / count : null;
}

export default SoribotTestRuns;